import React,
{
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Chart from 'react-apexcharts';
import ApexChartsLib, { ApexOptions } from 'apexcharts';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { i18n } from 'i18n';
import useProjectNumberFormatter from 'modules/project/providers/project/useProjectNumberFormatter';
import {
  CustomUnitFields,
  DisplayUnits,
  MetricPressureUnit,
  RheologyDataDto,
  UnitSystem
} from 'providers/api';
import { isNumber, round } from 'utils';
import { calculateYieldfromConcentrationByMass } from 'utils/backfill';
import {
  distanceMetricToImperial,
  flowRateMetricToImperial,
  frictionLossMetricToImperial,
  pumpPressureBarToKpa,
  pumpPressureKpaToBar,
  pumpPressureMetricToImperial,
  pressureMetricToImperial,
  volumeMetricToImperial,
  isCustomUnitField
} from 'utils/unitConversions';
import { additionalUnitLabels, BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { convertPressure, convertPumpPressure, convertkPaToM } from '../canvas/Model/Telemetry/conversions';
import PCRoute from '../MineModel/Route/PCRoute';
import { isChokeNode } from '../MineModel/Nodes';
import * as GConstants from '../MineModel/GConstants';
import { flushStageTypeLabel } from '../MineModel/Flushing/flushLabels';
import { FlushProfile } from '../MineModel/Flushing/flushTypes';
import {
  OptimisationTargetLineResult,
  OptimisationTargetType,
  PasteOptimiseResult,
  RecipeState
} from '../MineModel/types';
import {
  buildFlushOverlay,
  ChartPoint,
  flushDensityAt,
  FlushOverlayInput,
  RatingPoint
} from './flushOverlay';
import { exportHglCsv, HglCsvContext, HglPoint } from './hglCsvExport';
import { DistanceWindow, exportHglImage, HglImageContext } from './hglImageExport';
import calculateNiceInterval from './niceInterval';

/**
 * Legend name for a target line: its configured value and unit ("450 m³/h"), so two targets of the
 * same type are distinguishable. Targets are stored metric (kPa/m, bar, Pa, m³/h) and converted for
 * display only. Returns '' when the target has no value, leaving the caller to fall back.
 *
 * NOTE: this repeats the conversions in RecipeDetails and TargetLinesSection. They cannot share one
 * helper today because the stopes page and the reports page use two different OptimisationTargetType
 * enums (MineModel/types.ts vs providers/api) with identical members.
 */
const describeTargetLine = (
  target: OptimisationTargetLineResult,
  isImperial: boolean,
  isKiloPascal: boolean,
): string => {
  switch (target.targetType) {
    case OptimisationTargetType.KPaPerM: {
      if (target.kPaPerMTarget === undefined) return '';
      const unitInfo = additionalUnitLabels[DisplayUnits.FrictionLoss];
      if (isImperial) return `${round(frictionLossMetricToImperial(target.kPaPerMTarget), 3)} ${unitInfo.imperial.abbreviation}`;
      return `${target.kPaPerMTarget} ${unitInfo.metric.abbreviation}`;
    }
    case OptimisationTargetType.PumpPressure: {
      if (target.pumpPressureTarget === undefined) return '';
      const unitInfo = unitLabels[BasicDisplayUnits.PressurePump];
      if (isImperial) return `${round(pumpPressureMetricToImperial(target.pumpPressureTarget), 1)} ${unitInfo.imperial.abbreviation}`;
      if (isKiloPascal) return `${round(pumpPressureBarToKpa(target.pumpPressureTarget), 0)} kPa`;
      return `${target.pumpPressureTarget} ${unitInfo.metric.abbreviation}`;
    }
    case OptimisationTargetType.Yield:
      return target.yieldTarget === undefined ? '' : `${target.yieldTarget} Pa`;
    case OptimisationTargetType.FlowRate: {
      if (target.flowRateTarget === undefined) return '';
      const unitInfo = additionalUnitLabels[DisplayUnits.FlowRate];
      if (isImperial) return `${round(flowRateMetricToImperial(target.flowRateTarget), 1)} ${unitInfo.imperial.abbreviation}`;
      return `${target.flowRateTarget} ${unitInfo.metric.abbreviation}`;
    }
    default:
      return '';
  }
};

type SeriesObject = Extract<ApexOptions['series'], { name?: string }[]>[number];

type SeriesPoint = { x: number | undefined; y: number | undefined; kpa?: number };

// Choke stations and rupture disks are drawn as annotation dots, not series, so they carry their own
// legend entry alongside the points and are toggled outside ApexCharts' series mechanism. A group may
// instead mark positions with vertical lines (the flush pump start, the air start).
interface MarkerAnnotationGroup {
  name: string;
  color: string;
  points: {
    x: number;
    y: number;
    kpa?: number;
    marker: { size: number; fillColor: string; strokeColor: string; strokeWidth: number };
    label: { text: string };
  }[];
  lines?: number[];
}

interface ApexChartGraphProps {
  selectedRoute?: PCRoute;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  recipeState: RecipeState;
  optimiserResults?: PasteOptimiseResult | undefined;
  showMinMaxOnGraph?: boolean;
  showPressureLineOnHgl?: boolean;
  pipeRatingOffsetPercent?: number;
  metricPressureUnit?: MetricPressureUnit;
  flushOverlay?: FlushOverlayInput;
  rheologyDataset?: RheologyDataDto;
}

const toPercent = (concentration?: number) => {
  if (!isNumber(concentration) || concentration <= 0) return undefined;
  return concentration > 1 ? concentration : concentration * 100;
};

const distanceWindowOf = (chart: { w?: { globals?: { minX?: unknown; maxX?: unknown } } }): DistanceWindow | undefined => {
  const minX = chart?.w?.globals?.minX;
  const maxX = chart?.w?.globals?.maxX;
  return isNumber(minX) && isNumber(maxX) && maxX > minX ? { min: minX, max: maxX } : undefined;
};

interface HglChartContext {
  el: Element;
  w: { globals: { minX: number; maxX: number; seriesX: number[][] } };
}

interface HoverGuide {
  left: number;
  top: number;
  height: number;
}

const isChartContext = (chart: unknown): chart is HglChartContext => typeof chart === 'object' && chart !== null && 'el' in chart && 'w' in chart;

const plotAreaOf = (chart: HglChartContext) => chart.el?.querySelector('.apexcharts-grid')?.getBoundingClientRect();

const guideAt = (chart: HglChartContext, container: HTMLElement | null, distance: number): HoverGuide | null => {
  const plot = plotAreaOf(chart);
  if (!plot || !container) return null;
  const { minX, maxX } = chart.w.globals;
  const ratio = (distance - minX) / (maxX - minX);
  if (!(ratio >= 0 && ratio <= 1)) return null;
  const origin = container.getBoundingClientRect();
  return { left: plot.left - origin.left + ratio * plot.width, top: plot.top - origin.top, height: plot.height };
};

const nearestValue = (values: number[] | undefined, target: number) => (values ?? []).reduce<number | undefined>(
  (best, value) => (best === undefined || Math.abs(value - target) < Math.abs(best - target) ? value : best),
  undefined,
);

const CURSOR_GUIDE_COLOUR = '#b6b6b6';

const PRESSURE_LINE_NAME = 'Pressure';
// The literal has to sit inside i18n._() for lingui extract to register the msgid (extraction is static).
const flushLineName = () => i18n._('Flush HGL');

const PNG_EXPORT_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"'
  + ' stroke="#c4c4c4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-top:3px">'
  + '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';

const CSV_EXPORT_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"'
  + ' stroke="#c4c4c4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-top:3px">'
  + '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>'
  + '<path d="M12 13v5"/><path d="M9.5 15.5L12 18l2.5-2.5"/></svg>';

const CHART_HOVER_EVENT = 'hgl-chart-hover';
const ROUTE_HOVER_EVENT = 'hgl-route-hover';

const createSeries = (name: string, data?: SeriesPoint[], color?: string): SeriesObject | null => {
  const validData = data?.filter((p) => p.x !== undefined && p.y !== undefined) as HglPoint[] | undefined;
  if (!validData || validData.length === 0) {
    return null;
  }
  return {
    name,
    data: validData,
    color,
  };
};

// A marker group that only marks positions (the flush pump start, the air start) is drawn as vertical lines rather than dots.
const isLineGroup = (group: MarkerAnnotationGroup) => group.points.length === 0 && (group.lines?.length ?? 0) > 0;

const legendSwatchSx = ({ color, dashed, marker, vertical }: { color: string; dashed: boolean; marker: boolean; vertical: boolean }) => {
  if (vertical) return { width: 3, height: 10, backgroundColor: color };
  if (marker) return { width: 8, height: 8, borderRadius: '50%', backgroundColor: color };
  return { width: 16, borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${color}` };
};

const ApexChartGraph: React.FC<ApexChartGraphProps> = (props) => {
  const formatWithProjectSeparators = useProjectNumberFormatter();
  const {
    unitSystemPreference, customUnitFields, selectedRoute, recipeState, optimiserResults,
    showMinMaxOnGraph = false, showPressureLineOnHgl = false, pipeRatingOffsetPercent,
    metricPressureUnit = MetricPressureUnit.Bar, flushOverlay, rheologyDataset,
  } = props;
  const slurryDensity = recipeState?.wetDensity ?? 1800;
  const slackFlowSlurryDensity = optimiserResults?.slackFlowResult?.slurryDensity ?? 1800;
  const maxPressureSlurryDensity = optimiserResults?.maxPressureResult?.slurryDensity ?? 1800;

  const abbr = unitSystemPreference === UnitSystem.Imperial
    ? unitLabels[BasicDisplayUnits.Distance].imperial.abbreviation
    : unitLabels[BasicDisplayUnits.Distance].metric.abbreviation;

  const isCustomPressureUnit = isCustomUnitField(unitSystemPreference, customUnitFields, unitLabels[BasicDisplayUnits.PressureUCS].customUnitField);
  const pressureLineUnitAbbr = isCustomPressureUnit
    ? unitLabels[BasicDisplayUnits.PressureUCS].imperial.abbreviation
    : unitLabels[BasicDisplayUnits.PressureUCS].metric.abbreviation;
  const toDisplayPressure = (kpa: number) => (isCustomPressureUnit ? pressureMetricToImperial(kpa) : kpa);

  const seriesPressureKpaRef = useRef<Record<string, Map<number, number>>>({});
  // distanceFromPump -> choke station head, already converted to the project's display units.
  const chokeHeadByDistanceRef = useRef<Map<number, number>>(new Map());
  const isImperialRef = useRef(unitSystemPreference === UnitSystem.Imperial);
  const customUnitFieldsRef = useRef(customUnitFields);
  isImperialRef.current = unitSystemPreference === UnitSystem.Imperial;
  customUnitFieldsRef.current = customUnitFields;

  const latestCsvContextRef = useRef<HglCsvContext | null>(null);
  const latestImageContextRef = useRef<HglImageContext | null>(null);

  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [cursorGuide, setCursorGuide] = useState<HoverGuide | null>(null);
  const [routeGuide, setRouteGuide] = useState<HoverGuide | null>(null);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const [hiddenMarkers, setHiddenMarkers] = useState<string[]>([]);
  const [markerAnnotations, setMarkerAnnotations] = useState<MarkerAnnotationGroup[]>([]);
  const [options, setOptions] = useState<ApexOptions>({
    chart: {
      id: 'hgl-chart',
      events: {},
      toolbar: {
        show: true,
        // Lifted into the band reserved by grid.padding.top below.
        offsetY: -6,
        autoSelected: 'zoom',
        tools: {
          reset: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          selection: false,
          download: false,
          customIcons: [{
            icon: PNG_EXPORT_ICON,
            index: 0,
            title: i18n._('Download PNG (white background)'),
            class: 'hgl-light-export',
            click: (chart: any) => {
              const context = latestImageContextRef.current;
              if (context) exportHglImage(context, distanceWindowOf(chart));
            },
          }, {
            icon: CSV_EXPORT_ICON,
            index: 1,
            title: i18n._('Download CSV (all series)'),
            class: 'hgl-csv-export',
            // This toolbar is built once, when the options state is initialised, so the click closure has to read
            // a ref rather than the series and units of the render that created it.
            click: () => {
              const context = latestCsvContextRef.current;
              if (context) exportHglCsv(context);
            },
          }],
        },
      },
      background: '#303030',
      animations: {
        enabled: false,
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true,
      },
    },
    xaxis: {
      type: 'numeric',
      title: {
        text: i18n._('Distance from Pump ') + abbr,
        style: {
          color: '#FFFFFF',
        },
      },
      labels: {
        style: {
          colors: '#FFFFFF',
        },
        formatter: (value: string | number) => {
          if (typeof value === 'number') return formatWithProjectSeparators(value, 0);
          return value;
        },
      },
      tickAmount: 10,
      // ApexCharts places its crosshair, hover dot and tooltip box at "point j of the series nearest the cursor", which assumes
      // every series has a point at each index. Pipe Rating has two points per node, so above it they all landed at the wrong
      // distance. This component draws the crosshair instead, the hover dot is off, and the tooltip follows the cursor.
      crosshairs: {
        show: false,
      },
    },
    markers: {
      size: 0,
      hover: { size: 0 },
    },
    yaxis: {
      forceNiceScale: true,
      title: {
        text: `${i18n._('Elevation')} (${abbr})`,
        style: {
          color: '#FFFFFF',
        },
      },
      labels: {
        style: {
          colors: '#FFFFFF',
        },
        formatter: (value: string | number) => {
          if (typeof value === 'number') return formatWithProjectSeparators(value, 0);
          return value;
        },
      },
    },
    stroke: {
      curve: 'straight',
      width: 2,
    },
    tooltip: {
      theme: 'dark',
      shared: true,
      intersect: false,
      followCursor: true,
      custom: ({ series, dataPointIndex, w }: { series: number[][]; seriesIndex: number; dataPointIndex: number; w: unknown }) => {
        const wGlobals = (w as { globals: { seriesNames: string[]; colors: string[]; seriesX: number[][] } }).globals;
        const { seriesNames, colors } = wGlobals;
        const elevationName = i18n._('Pipe Profile');
        const transientName = i18n._('Transient');
        const pressureLineName = i18n._(PRESSURE_LINE_NAME);
        const xVal = wGlobals.seriesX?.[0]?.[dataPointIndex];
        const distUnit = isImperialRef.current ? 'ft' : 'm';
        const xLabel = typeof xVal === 'number' ? `${formatWithProjectSeparators(xVal, 1)} ${distUnit}` : '';

        const yByName = (target: string): number | undefined => {
          const idx = seriesNames.indexOf(target);
          if (idx < 0) return undefined;
          const v = series[idx]?.[dataPointIndex];
          return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
        };
        const colorByName = (target: string): string => {
          const idx = seriesNames.indexOf(target);
          return (idx >= 0 && colors[idx]) || GConstants.WHITE;
        };
        const renderRow = (color: string, label: string, value: string) => (
          '<div style=\'display:flex;align-items:center;gap:6px;padding:2px 0;\'>'
          + `<span style='width:10px;height:10px;background:${color};display:inline-block;border-radius:50%;'></span>`
          + `<span>${label}: <b>${value}</b></span>`
          + '</div>'
        );

        const rows = seriesNames.reduce((acc: string[], name: string, idx: number) => {
          if (name === elevationName || name === transientName || name === pressureLineName) return acc;
          const yVal = series[idx]?.[dataPointIndex];
          if (typeof yVal !== 'number' || Number.isNaN(yVal)) return acc;

          const kpaLookup = seriesPressureKpaRef.current[name];
          const kpa = typeof xVal === 'number' ? kpaLookup?.get(xVal) : undefined;
          const pressureLabel = typeof kpa === 'number'
            ? convertPressure(kpa, isImperialRef.current, customUnitFieldsRef.current)
            : '-';

          acc.push(renderRow(colors[idx], name, pressureLabel));
          return acc;
        }, []);

        const rdKpaMap = seriesPressureKpaRef.current[i18n._('Rupture Disk')];
        const rdKpa = typeof xVal === 'number' ? rdKpaMap?.get(xVal) : undefined;
        if (typeof rdKpa === 'number') {
          const rdLabel = convertPressure(rdKpa, isImperialRef.current, customUnitFieldsRef.current);
          rows.push(renderRow(GConstants.LIGHT_BLUE, i18n._('Rupture Disk'), rdLabel));
        }

        const chokeHead = typeof xVal === 'number' ? chokeHeadByDistanceRef.current.get(xVal) : undefined;
        if (typeof chokeHead === 'number') {
          rows.push(renderRow(GConstants.ORANGE, i18n._('Choke Station'), `-${formatWithProjectSeparators(chokeHead, 1)} ${distUnit}`));
        }

        // Elevation of the pipe profile at the hovered distance (always shown when available).
        const elevationY = yByName(elevationName);
        const elevationRow = typeof elevationY === 'number'
          ? renderRow(colorByName(elevationName), i18n._('Elevation'), `${formatWithProjectSeparators(elevationY, 1)} ${distUnit}`)
          : '';

        // Pressure line value, only present when the Pressure series is enabled (showPressureLineOnHgl).
        const pressureY = yByName(pressureLineName);
        const pressureUnitAbbr = isCustomUnitField(
          isImperialRef.current ? UnitSystem.Imperial : UnitSystem.Metric,
          customUnitFieldsRef.current,
          unitLabels[BasicDisplayUnits.PressureUCS].customUnitField,
        )
          ? unitLabels[BasicDisplayUnits.PressureUCS].imperial.abbreviation
          : unitLabels[BasicDisplayUnits.PressureUCS].metric.abbreviation;
        const pressureRow = typeof pressureY === 'number'
          ? renderRow(colorByName(pressureLineName), pressureLineName, `${formatWithProjectSeparators(pressureY, 0)} ${pressureUnitAbbr}`)
          : '';

        return '<div style=\'background:#1e1e1e;border:1px solid #444;padding:8px 10px;border-radius:4px;font-size:12px;color:#fff;\'>'
          + `<div style='margin-bottom:4px;font-weight:bold;'>${xLabel}</div>`
          + `${elevationRow}`
          + `${rows.join('')}`
          + `${pressureRow}`
          + '</div>';
      },
    },
    grid: {
      borderColor: '#555',
      // apexcharts 5.16 renders the toolbar as a filled rounded box rather than bare icons, so it now
      // sits ON the plot instead of beside it. Reserving a band at the top gives it its own space and
      // keeps the series clear of it.
      padding: { top: 28 },
      row: {
        colors: ['transparent', 'transparent'],
        opacity: 0.5,
      },
    },
    legend: {
      show: false,
    },
    theme: {
      mode: 'dark',
    },
  });

  const dispatchChartHover = useCallback((distance: number | null) => {
    window.dispatchEvent(new CustomEvent(CHART_HOVER_EVENT, { detail: { distance } }));
  }, []);

  const handleChartMouseMove = useCallback((event: MouseEvent, chartContext: unknown) => {
    if (!isChartContext(chartContext)) return;
    const gridRect = plotAreaOf(chartContext);
    const { minX, maxX, seriesX } = chartContext.w.globals;
    if (!gridRect || typeof minX !== 'number' || typeof maxX !== 'number' || maxX === minX) return;

    const pointerX = event.clientX - gridRect.left;
    if (pointerX < 0 || pointerX > gridRect.width) {
      dispatchChartHover(null);
      setCursorGuide(null);
      return;
    }

    const distance = minX + (maxX - minX) * (pointerX / gridRect.width);
    dispatchChartHover(distance);
    setCursorGuide(guideAt(chartContext, chartAreaRef.current, nearestValue(seriesX?.[0], distance) ?? distance));
  }, [dispatchChartHover]);

  const handleDataPointHover = useCallback((_event: any, _chartContext: any, config: any) => {
    const xValue = config?.w?.config?.series?.[config.seriesIndex]?.data?.[config.dataPointIndex]?.x;
    dispatchChartHover(typeof xValue === 'number' ? xValue : null);
  }, [dispatchChartHover]);

  const handleChartMouseLeave = useCallback(() => {
    dispatchChartHover(null);
    setCursorGuide(null);
  }, [dispatchChartHover]);

  useEffect(() => {
    const handleRouteHover = (evt: Event) => {
      const distance = (evt as CustomEvent<{ distance: number | null }>).detail?.distance;
      const chart = ApexChartsLib.getChartByID('hgl-chart');
      setRouteGuide(typeof distance === 'number' && isChartContext(chart) ? guideAt(chart, chartAreaRef.current, distance) : null);
    };
    window.addEventListener(ROUTE_HOVER_EVENT, handleRouteHover);
    return () => window.removeEventListener(ROUTE_HOVER_EVENT, handleRouteHover);
  }, []);

  const [series, setSeries] = useState<ApexOptions['series']>([]);

  useEffect(() => {
    const updateSeriesAndCaptureImage = () => {
      if (selectedRoute?.routePoints && selectedRoute.routePoints.length > 0) {
        const { routePoints } = selectedRoute;
        const slackRoutePoints = optimiserResults?.slackFlowResult?.route.routePoints ?? [];
        const maxPressureRoutePoints = optimiserResults?.maxPressureResult?.route.routePoints ?? [];
        const elevationData: { x: number | undefined; y: number | undefined }[] = [];
        const pressureData: SeriesPoint[] = [];
        const pressureLineData: { x: number | undefined; y: number | undefined }[] = [];
        const pipeRaw: { x: number | undefined; y: number | undefined; kpa: number }[] = [];
        const slackFlowData: SeriesPoint[] = [];
        const maxPressureData: SeriesPoint[] = [];
        const ruptureDiskData: SeriesPoint[] = [];
        const chokeStationData: { x: number | undefined; y: number | undefined }[] = [];
        const chokeHeadMap = new Map<number, number>();

        const kpaLookup: Record<string, Map<number, number>> = {};
        const pressureKpaMap = new Map<number, number>();
        const pipeRatingKpaMap = new Map<number, number>();
        const slackFlowKpaMap = new Map<number, number>();
        const maxPressureKpaMap = new Map<number, number>();
        const ruptureDiskKpaMap = new Map<number, number>();

        // While a flush is shown, pressure-as-head follows the fluid at each point: water behind the front, paste ahead.
        const densityAt = flushOverlay ? flushDensityAt(flushOverlay.profile, slurryDensity) : () => slurryDensity;
        const pipeRatingRaw: RatingPoint[] = [];

        routePoints.forEach((point) => {
          const elevation = point.point.coords.z;
          const pipeUpstreamPressure = convertkPaToM(point.pipeUpstream?.pressureRating, slurryDensity);
          const pipeDownstreamPressure = convertkPaToM(point.pipeDownstream?.pressureRating, slurryDensity);
          const pressure = convertkPaToM(point.pressure, slurryDensity);

          // Convert distance based on unit preference
          const distance = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(point.distanceFromPump || 0)
            : point.distanceFromPump || 0;
          const elevationConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(elevation)
            : elevation;
          const pressureConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(pressure)
            : pressure;
          const pipeUpstreamConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(pipeUpstreamPressure)
            : pipeUpstreamPressure;
          const pipeDownstreamConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(pipeDownstreamPressure)
            : pipeDownstreamPressure;

          elevationData.push({
            x: distance,
            y: elevationConverted,
          });

          pressureData.push({
            x: distance,
            y: pressureConverted + elevationConverted,
            kpa: point.pressure ?? 0,
          });
          pressureKpaMap.set(distance, point.pressure ?? 0);

          pressureLineData.push({
            x: distance,
            y: toDisplayPressure(point.pressure ?? 0),
          });

          if ((point.point.ruptureDiskPressure ?? 0) > 0) {
            const ruptureDiskPressureHead = convertkPaToM(point.point.ruptureDiskPressure, densityAt(point.distanceFromPump || 0));
            const ruptureDiskPressureHeadConverted = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(ruptureDiskPressureHead)
              : ruptureDiskPressureHead;
            ruptureDiskData.push({
              x: distance,
              y: ruptureDiskPressureHeadConverted + elevationConverted,
              kpa: point.point.ruptureDiskPressure ?? 0,
            });
            ruptureDiskKpaMap.set(distance, point.point.ruptureDiskPressure ?? 0);
          }

          // The choke twin sits at the same distance as its original node but carries the extra head, so it is
          // the top of the vertical HGL step. Mark it and record the head for the tooltip.
          if (isChokeNode(point.point.id) && point.point.chokeHead > 0) {
            chokeStationData.push({
              x: distance,
              y: pressureConverted + elevationConverted,
            });
            chokeHeadMap.set(distance, unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(point.point.chokeHead)
              : point.point.chokeHead);
          }

          if (point.pipeDownstream) {
            pipeRaw.push({
              x: distance,
              y: pipeDownstreamConverted + elevationConverted,
              kpa: point.pipeDownstream.pressureRating ?? 0,
            });
            pipeRatingRaw.push({ distance: point.distanceFromPump || 0, elevation, kpa: point.pipeDownstream.pressureRating ?? 0 });
          }

          if (point.pipeUpstream) {
            pipeRaw.push({
              x: distance,
              y: pipeUpstreamConverted + elevationConverted,
              kpa: point.pipeUpstream.pressureRating ?? 0,
            });
            pipeRatingRaw.push({ distance: point.distanceFromPump || 0, elevation, kpa: point.pipeUpstream.pressureRating ?? 0 });
          }

          const downKpa = point.pipeDownstream?.pressureRating;
          const upKpa = point.pipeUpstream?.pressureRating;
          if (typeof downKpa === 'number' && typeof upKpa === 'number') {
            pipeRatingKpaMap.set(distance, Math.min(downKpa, upKpa));
          } else if (typeof downKpa === 'number') {
            pipeRatingKpaMap.set(distance, downKpa);
          } else if (typeof upKpa === 'number') {
            pipeRatingKpaMap.set(distance, upKpa);
          }
        });

        // While a flush is shown, the rating line follows the same local-fluid basis and steps at the front.
        const flush = flushOverlay
          ? buildFlushOverlay(flushOverlay, slurryDensity, unitSystemPreference === UnitSystem.Imperial, pipeRatingRaw)
          : undefined;
        if (flush) kpaLookup[flushLineName()] = flush.kpaByX;
        if (flush?.frontRatingKpa !== undefined) pipeRatingKpaMap.set(flush.waterFront.x, flush.frontRatingKpa);

        const pipeFiltered = pipeRaw.filter((p) => p.x !== undefined && p.y !== undefined);
        const pipeData: SeriesPoint[] = flush ? flush.pipeRatingData : pipeFiltered;

        slackRoutePoints.forEach((point) => {
          const elevation = point.point.coords.z;
          const pressure = convertkPaToM(point.pressure, slackFlowSlurryDensity);
          const distance = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(point.distanceFromPump || 0)
            : point.distanceFromPump || 0;
          const elevationConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(elevation)
            : elevation;
          const pressureConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(pressure)
            : pressure;
          slackFlowData.push({
            x: distance,
            y: pressureConverted + elevationConverted,
            kpa: point.pressure ?? 0,
          });
          slackFlowKpaMap.set(distance, point.pressure ?? 0);
        });

        maxPressureRoutePoints.forEach((point) => {
          const elevation = point.point.coords.z;
          const pressure = convertkPaToM(point.pressure, maxPressureSlurryDensity);
          const distance = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(point.distanceFromPump || 0)
            : point.distanceFromPump || 0;
          const elevationConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(elevation)
            : elevation;
          const pressureConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(pressure)
            : pressure;
          maxPressureData.push({
            x: distance,
            y: pressureConverted + elevationConverted,
            kpa: point.pressure ?? 0,
          });
          maxPressureKpaMap.set(distance, point.pressure ?? 0);
        });

        const transientData: { x: number | undefined; y: number | undefined }[] = [];

        const transientPumpPressureKpa = selectedRoute?.pumpPressureKpa
          || optimiserResults?.optimisedResult?.actualPumpPressureKpa
          || recipeState?.pumpPressureKpa;

        const showTransient = pipeRatingOffsetPercent !== undefined && pipeRatingOffsetPercent > 0;
        if (showTransient && transientPumpPressureKpa) {
          const pumpPressureInMetres = convertkPaToM(transientPumpPressureKpa, slurryDensity);
          const transientOffsetMetres = pumpPressureInMetres * (pipeRatingOffsetPercent / 100);
          const transientOffsetConverted = unitSystemPreference === UnitSystem.Imperial
            ? distanceMetricToImperial(transientOffsetMetres)
            : transientOffsetMetres;

          routePoints.forEach((point) => {
            const elevation = point.point.coords.z;
            const pressure = convertkPaToM(point.pressure, slurryDensity);
            const distance = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(point.distanceFromPump || 0)
              : point.distanceFromPump || 0;
            const elevationConverted = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(elevation)
              : elevation;
            const pressureConverted = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(pressure)
              : pressure;

            transientData.push({
              x: distance,
              y: pressureConverted + elevationConverted + transientOffsetConverted,
            });
          });
        }

        const usedTargetNames = new Set<string>();
        const targetLineSeriesData = (optimiserResults?.targetLineResults ?? []).map((tl, idx) => {
          const tlRoutePoints = tl.result?.route?.routePoints ?? [];
          const tlSlurryDensity = tl.result?.slurryDensity ?? 1800;
          const data: SeriesPoint[] = [];
          const tlKpaMap = new Map<number, number>();
          tlRoutePoints.forEach((point) => {
            const elevation = point.point.coords.z;
            const pressure = convertkPaToM(point.pressure, tlSlurryDensity);
            const distance = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(point.distanceFromPump || 0) : point.distanceFromPump || 0;
            const elevationConverted = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(elevation) : elevation;
            const pressureConverted = unitSystemPreference === UnitSystem.Imperial
              ? distanceMetricToImperial(pressure) : pressure;
            data.push({ x: distance, y: pressureConverted + elevationConverted, kpa: point.pressure ?? 0 });
            tlKpaMap.set(distance, point.pressure ?? 0);
          });
          // Name the series by its target value ("450 m³/h") so two targets of the same type are
          // distinguishable. Series names key kpaLookup and drive the tooltip, so they must stay
          // unique — fall back to the positional name if a value is missing or already taken.
          const describedName = describeTargetLine(
            tl,
            unitSystemPreference === UnitSystem.Imperial,
            metricPressureUnit === MetricPressureUnit.KiloPascal,
          );
          const seriesName = describedName && !usedTargetNames.has(describedName) ? describedName : `Target ${idx + 1}`;
          usedTargetNames.add(seriesName);
          kpaLookup[seriesName] = tlKpaMap;
          const targetColors = [GConstants.YELLOW, GConstants.ORANGE, GConstants.PURPLE, GConstants.LIGHT_BLUE, GConstants.LIGHT_RED];
          return createSeries(seriesName, data, targetColors[idx % targetColors.length]);
        });

        kpaLookup[i18n._('HGL')] = pressureKpaMap;
        kpaLookup[i18n._('Pipe Rating')] = pipeRatingKpaMap;
        kpaLookup[i18n._('Slack Flow')] = slackFlowKpaMap;
        kpaLookup[i18n._('Max Pressure')] = maxPressureKpaMap;
        kpaLookup[i18n._('Rupture Disk')] = ruptureDiskKpaMap;
        seriesPressureKpaRef.current = kpaLookup;
        chokeHeadByDistanceRef.current = chokeHeadMap;

        const newSeries = [
          createSeries(i18n._('Pipe Profile'), elevationData, GConstants.GREEN),
          createSeries(i18n._('Pipe Rating'), pipeData, GConstants.RED),
          showMinMaxOnGraph ? createSeries(i18n._('Max Pressure'), maxPressureData, GConstants.DARK_RED) : null,
          createSeries(i18n._('HGL'), pressureData, GConstants.WHITE),
          flush ? createSeries(flushLineName(), flush.hglData, GConstants.DEEP_SKY_BLUE) : null,
          showPressureLineOnHgl ? createSeries(i18n._(PRESSURE_LINE_NAME), pressureLineData, GConstants.MEDIUM_BLUE) : null,
          showMinMaxOnGraph ? createSeries(i18n._('Slack Flow'), slackFlowData, GConstants.CYAN) : null,
          showTransient ? createSeries(i18n._('Transient'), transientData, GConstants.ORANGE) : null,
          ...targetLineSeriesData,
        ].filter((s): s is SeriesObject => s !== null);

        const dashArray = newSeries.map((s) => (s.name === i18n._('Transient') || s.name === i18n._(PRESSURE_LINE_NAME) ? 5 : 0));

        const ruptureDiskAnnotations = ruptureDiskData
          .filter((p) => typeof p.x === 'number' && typeof p.y === 'number')
          .map((p) => ({
            x: p.x as number,
            y: p.y as number,
            kpa: p.kpa,
            marker: {
              size: 4,
              fillColor: GConstants.LIGHT_BLUE,
              strokeColor: GConstants.BLACK,
              strokeWidth: 1,
            },
            label: { text: '' },
          }));

        const chokeStationAnnotations = chokeStationData
          .filter((p) => typeof p.x === 'number' && typeof p.y === 'number')
          .map((p) => ({
            x: p.x as number,
            y: p.y as number,
            marker: {
              size: 4,
              fillColor: GConstants.ORANGE,
              strokeColor: GConstants.BLACK,
              strokeWidth: 1,
            },
            label: { text: '' },
          }));

        const flushMarker = (point: ChartPoint, fillColor: string) => ({
          x: point.x,
          y: point.y,
          marker: {
            size: 5,
            fillColor,
            strokeColor: GConstants.WHITE,
            strokeWidth: 1,
          },
          label: { text: '' },
        });

        setSeries(newSeries);
        setMarkerAnnotations([
          { name: i18n._('Rupture Disk'), color: GConstants.LIGHT_BLUE, points: ruptureDiskAnnotations },
          { name: i18n._('Choke Station'), color: GConstants.ORANGE, points: chokeStationAnnotations },
          { name: i18n._('Water Front'), color: GConstants.SKY_BLUE, points: flush ? [flushMarker(flush.waterFront, GConstants.SKY_BLUE)] : [] },
          { name: i18n._('Flush Pump Start'), color: GConstants.MINT_GREEN, points: [], lines: flush?.handoverX !== undefined ? [flush.handoverX] : [] },
          { name: i18n._('Air Start'), color: GConstants.SOFT_YELLOW, points: [], lines: flush?.airStartX !== undefined ? [flush.airStartX] : [] },
        ].filter((group) => group.points.length > 0 || (group.lines?.length ?? 0) > 0));
        setOptions((prev) => ({
          ...prev,
          stroke: { ...prev.stroke, dashArray, width: 2 },
          markers: { ...(prev.markers || {}), size: 0 },
        }));

        const allYValues = [
          ...elevationData.map((p) => p.y),
          ...pressureData.map((p) => p.y),
          ...pipeData.map((p) => p.y),
          ...ruptureDiskData.map((p) => p.y),
          ...chokeStationData.map((p) => p.y),
          ...(flush ? flush.hglData.map((p) => p.y) : []),
          ...(showTransient ? transientData.map((p) => p.y) : []),
        ].filter((y) => typeof y === 'number') as number[];

        if (allYValues.length > 0) {
          const minY = Math.min(...allYValues);
          const maxY = Math.max(...allYValues);
          const padding = (maxY - minY) * 0.1;

          // Calculate nice intervals for Y-axis
          const yAxisNice = calculateNiceInterval(minY - padding, maxY + padding, 8, 'y');

          // Calculate nice intervals for X-axis based on distance data
          const allXValues = [...elevationData, ...pressureData, ...pipeData]
            .map((p) => p.x)
            .filter((x) => typeof x === 'number') as number[];

          let xAxisOptions = {};
          if (allXValues.length > 0) {
            const minX = Math.min(...allXValues);
            const maxX = Math.max(...allXValues);
            const xAxisNice = calculateNiceInterval(minX, maxX, 10, 'x');

            xAxisOptions = {
              min: xAxisNice.niceMin,
              max: xAxisNice.niceMax,
              tickAmount: xAxisNice.tickAmount,
            };
          }

          const pressureLineYValues = pressureLineData
            .map((p) => p.y)
            .filter((y): y is number => typeof y === 'number');
          const showSecondaryAxis = showPressureLineOnHgl && pressureLineYValues.length > 0;

          const secondaryYAxis = showSecondaryAxis
            ? (() => {
              const secNice = calculateNiceInterval(Math.min(...pressureLineYValues), Math.max(...pressureLineYValues), 8, 'y');
              return {
                opposite: true,
                seriesName: i18n._(PRESSURE_LINE_NAME),
                forceNiceScale: true,
                min: secNice.niceMin,
                max: secNice.niceMax,
                tickAmount: secNice.tickAmount,
                title: {
                  text: `${i18n._('Pressure')} (${pressureLineUnitAbbr})`,
                  style: { color: '#FFFFFF' },
                },
                labels: {
                  style: { colors: '#FFFFFF' },
                  formatter: (value: string | number) => (typeof value === 'number' ? formatWithProjectSeparators(value, 0) : value),
                },
              };
            })()
            : null;

          const primarySeriesNames = newSeries
            .map((s) => s.name)
            .filter((name): name is string => typeof name === 'string' && name !== i18n._(PRESSURE_LINE_NAME));

          setOptions((prevOptions) => {
            const prevPrimary = Array.isArray(prevOptions.yaxis) ? prevOptions.yaxis[0] : prevOptions.yaxis;
            const primaryYAxis = {
              ...prevPrimary,
              min: yAxisNice.niceMin,
              max: yAxisNice.niceMax,
              tickAmount: yAxisNice.tickAmount,
              seriesName: secondaryYAxis ? primarySeriesNames : undefined,
            };
            return {
              ...prevOptions,
              xaxis: {
                ...prevOptions.xaxis,
                ...xAxisOptions,
              },
              yaxis: secondaryYAxis ? [primaryYAxis, secondaryYAxis] : primaryYAxis,
            };
          });
        }
      } else {
        setSeries([]);
        setMarkerAnnotations([]);
      }
    };

    updateSeriesAndCaptureImage();
  }, [selectedRoute, unitSystemPreference, customUnitFields, recipeState, optimiserResults, pipeRatingOffsetPercent, showMinMaxOnGraph, showPressureLineOnHgl, flushOverlay]);

  const chartEvents = useMemo(() => ({
    mouseMove: handleChartMouseMove,
    dataPointMouseEnter: handleDataPointHover,
    dataPointMouseLeave: handleChartMouseLeave,
    mouseLeave: handleChartMouseLeave,
  }), [handleChartMouseMove, handleDataPointHover, handleChartMouseLeave]);

  const computedOptions = useMemo(() => ({
    ...options,
    annotations: {
      ...(options.annotations || {}),
      points: markerAnnotations
        .filter((group) => !hiddenMarkers.includes(group.name))
        .flatMap((group) => group.points),
      xaxis: [
        ...markerAnnotations
          .filter((group) => !hiddenMarkers.includes(group.name))
          .flatMap((group) => (group.lines ?? []).map((x) => ({
            x,
            borderColor: group.color,
            strokeDashArray: 0,
            opacity: 0.9,
            label: {
              text: group.name,
              orientation: 'horizontal',
              position: 'top',
              borderColor: group.color,
              style: { color: GConstants.BLACK, background: group.color, fontSize: '10px' },
            },
          }))),
      ],
    },
    chart: {
      ...options.chart,
      events: {
        ...(options.chart?.events || {}),
        ...chartEvents,
      },
    },
  }), [options, markerAnnotations, hiddenMarkers, chartEvents]);

  const plottedSeries = (Array.isArray(series) ? (series as SeriesObject[]) : [])
    .filter((s) => Boolean(s?.name) && Array.isArray(s.data))
    .map((s) => ({ name: s.name as string, data: s.data as HglPoint[], color: s.color }));

  latestCsvContextRef.current = {
    series: plottedSeries,
    markerGroups: markerAnnotations.map(({ name, points }) => ({ name, data: points })),
    chokeHeadByDistance: chokeHeadByDistanceRef.current,
    distanceAbbr: abbr,
    pressureSeriesName: i18n._(PRESSURE_LINE_NAME),
    pressureAbbr: pressureLineUnitAbbr,
    stopeName: selectedRoute?.stope?.data?.stopeName,
  };

  const toggleSeries = useCallback((name: string) => {
    ApexChartsLib.exec('hgl-chart', 'toggleSeries', name);
    setHiddenSeries((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }, []);

  const toggleMarkerGroup = useCallback((name: string) => {
    setHiddenMarkers((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }, []);

  const legendItems = useMemo(
    () => [
      ...(Array.isArray(series) ? (series as SeriesObject[]) : [])
        .filter((s): s is SeriesObject => Boolean(s && s.name))
        .map((s) => ({
          name: s.name as string,
          color: (s.color as string) || GConstants.WHITE,
          dashed: s.name === i18n._('Transient') || s.name === i18n._(PRESSURE_LINE_NAME),
          marker: false,
          vertical: false,
        })),
      ...markerAnnotations.map((group) => ({
        name: group.name,
        color: group.color,
        dashed: false,
        marker: true,
        vertical: isLineGroup(group),
      })),
    ],
    [series, markerAnnotations],
  );

  const pumpPressureKpa = selectedRoute?.pumpPressureKpa
    || optimiserResults?.optimisedResult?.actualPumpPressureKpa
    || recipeState?.pumpPressureKpa;

  const isImperialProject = unitSystemPreference === UnitSystem.Imperial;
  const massConcentrationPercent = toPercent(recipeState?.totalDryConcentrationByMass);
  const yieldStressCoefficients = rheologyDataset?.yieldStressCoefficients;
  const wetFlowRate = recipeState?.wetFlowRate;
  const flowRateUnit = additionalUnitLabels[DisplayUnits.FlowRate];

  latestImageContextRef.current = {
    series: plottedSeries.filter(({ name }) => !hiddenSeries.includes(name)),
    markerGroups: markerAnnotations
      .filter(({ name }) => !hiddenMarkers.includes(name))
      .map(({
        name, color, points, lines,
      }) => ({
        name, color, data: points, lines: lines ?? [],
      })),
    summary: {
      stopeName: selectedRoute?.stope?.data?.stopeName,
      pumpPressure: pumpPressureKpa ? toDisplayPressure(pumpPressureKpa) : undefined,
      yieldStressPa: yieldStressCoefficients && isNumber(massConcentrationPercent)
        ? calculateYieldfromConcentrationByMass(massConcentrationPercent, yieldStressCoefficients.coefficient1, yieldStressCoefficients.coefficient2)
        : undefined,
      massConcentrationPercent,
      flowRate: isNumber(wetFlowRate) && isImperialProject ? flowRateMetricToImperial(wetFlowRate) : wetFlowRate,
      flowRateAbbr: isImperialProject ? flowRateUnit.imperial.abbreviation : flowRateUnit.metric.abbreviation,
    },
    distanceAbbr: abbr,
    pressureSeriesName: i18n._(PRESSURE_LINE_NAME),
    pressureAbbr: pressureLineUnitAbbr,
    dashedSeriesNames: [i18n._('Transient')],
    formatNumber: formatWithProjectSeparators,
  };

  const getPumpPressureDisplay = () => {
    if (!pumpPressureKpa) return null;

    const pumpPressureBar = pumpPressureKpaToBar(pumpPressureKpa);
    if (unitSystemPreference === UnitSystem.Imperial) {
      return convertPumpPressure(pumpPressureBar, true, customUnitFields, metricPressureUnit);
    }
    return convertPumpPressure(pumpPressureBar, false, customUnitFields, metricPressureUnit);
  };

  const pumpPressureDisplay = getPumpPressureDisplay();

  const describeFlush = (profile: FlushProfile): string => {
    const isImperial = unitSystemPreference === UnitSystem.Imperial;
    const volumeUnit = unitLabels[BasicDisplayUnits.Volume];
    const isCustomVolumeUnit = isCustomUnitField(unitSystemPreference, customUnitFields, volumeUnit.customUnitField);
    const volume = isCustomVolumeUnit ? volumeMetricToImperial(profile.waterVolume) : profile.waterVolume;
    const volumeAbbr = isCustomVolumeUnit ? volumeUnit.imperial.abbreviation : volumeUnit.metric.abbreviation;
    const front = isImperial ? distanceMetricToImperial(profile.frontDistance) : profile.frontDistance;
    const pressure = convertPumpPressure(pumpPressureKpaToBar(profile.requiredPressureKpa), isImperial, customUnitFields, metricPressureUnit);
    return [
      `${formatWithProjectSeparators(volume, 1)} ${volumeAbbr} ${i18n._('pumped')}`,
      `${i18n._('water front at')} ${formatWithProjectSeparators(front, 0)} ${abbr}`,
      flushStageTypeLabel(profile.drivingPump),
      pressure,
    ].join(' · ');
  };
  const flushDisplay = flushOverlay ? describeFlush(flushOverlay.profile) : null;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: `${100}%`,
          height: '100%',
          transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {legendItems.length > 0 && (
          <Box
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              columnGap: '12px',
              rowGap: '2px',
              px: 1,
              py: 0.5,
            }}
          >
            {legendItems.map((item) => {
              const isHidden = item.marker ? hiddenMarkers.includes(item.name) : hiddenSeries.includes(item.name);
              return (
                <Box
                  key={item.name}
                  onClick={() => (item.marker ? toggleMarkerGroup(item.name) : toggleSeries(item.name))}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    opacity: isHidden ? 0.4 : 1,
                  }}
                >
                  <Box component="span" sx={legendSwatchSx(item)} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '11px',
                      textDecoration: isHidden ? 'line-through' : 'none',
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        <Box ref={chartAreaRef} sx={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
          {Array.isArray(series) && series.length > 0 && (
            <>
              <Chart
                options={computedOptions}
                series={series}
                type="line"
                width="100%"
                height="100%"
              />
              {[
                { kind: 'cursor', guide: cursorGuide, colour: CURSOR_GUIDE_COLOUR },
                { kind: 'route', guide: routeGuide, colour: GConstants.YELLOW },
              ].map(({ kind, guide, colour }) => guide && (
                <Box
                  key={kind}
                  sx={{ position: 'absolute', zIndex: 1, pointerEvents: 'none', borderLeft: `1px dashed ${colour}` }}
                  style={{ left: guide.left, top: guide.top, height: guide.height }}
                />
              ))}
            </>
          )}
        </Box>

        {/* Pump Pressure Display */}
        {pumpPressureDisplay && (
          <Box
            sx={{
              flex: '0 0 auto',
              textAlign: 'left',
              color: '#FFFFFF',
              padding: '2px 4px 2px 40px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginTop: '2px',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', color: '#FFFFFF' }}>
              {i18n._('Pump Pressure')}
              :
              {' '}
              {pumpPressureDisplay}
              {' '}
            </Typography>
          </Box>
        )}

        {flushDisplay && (
          <Box
            sx={{
              flex: '0 0 auto',
              textAlign: 'left',
              color: '#FFFFFF',
              padding: '2px 4px 2px 40px',
              fontSize: '12px',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', color: GConstants.DEEP_SKY_BLUE }}>
              {i18n._('Flush')}
              :
              {' '}
              {flushDisplay}
            </Typography>
          </Box>
        )}
      </div>
    </Box>
  );
};

export default ApexChartGraph;
